import styled from '@emotion/styled';
import { useState, useMemo, useCallback, type ReactNode } from 'react';
import { CheckCircle } from 'lucide-react';
import type { UserProfile } from '@/lib/services/auth.service';
import type { CustomerSubscription, SubscriptionItem } from '@/domain/entities/subscription.entity';
import type { ProductVariant } from '@/domain/entities/product.entity';
import type { CollectionProduct } from '@/domain/entities/collection.entity';
import {
  useCustomerSubscriptions,
  useCancelSubscription,
  useSkipSubscription,
  useRescheduleSubscription,
  useUpdateSubscriptionItems,
} from '@/lib/hooks/use-customer-subscriptions';
import { SubscriptionCard } from './SubscriptionCard';
import { RetentionOfferPage } from './RetentionOfferPage';
import { RetentionFeedbackPage } from './RetentionFeedbackPage';
import { CancellationReasonPage } from './CancellationReasonPage';
import { SuccessToast } from './SuccessToast';
import { ManageSubscriptionPage } from './ManageSubscriptionPage';
import { SwapProductBrowsePage } from './SwapProductBrowsePage';
import { SwapProductDetailPage } from './SwapProductDetailPage';
import { SwapConfirmationDialog } from './SwapConfirmationDialog';
import { ChangeVariantSheet } from './ChangeVariantSheet';
import { SkipConfirmationSheet } from './SkipConfirmationSheet';
import { RescheduleSheet } from './RescheduleSheet';

// -- Styled Components --

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SectionTitle = styled.h3<{ readonly $variant: ActiveCardLayout }>`
  font-size: ${({ $variant }) => ($variant === 'list' ? '16px' : '18px')};
  font-weight: 600;
  line-height: ${({ $variant }) => ($variant === 'list' ? '1.5' : '1.56')};
  color: #292929;
  margin: 0;

  ${({ $variant }) =>
    $variant === 'list' &&
    `
    @media (min-width: 768px) {
      font-size: 18px;
      line-height: 1.44;
    }
  `}
`;

const ActiveIcon = styled.span`
  display: inline-flex;
  color: #4cbea4;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
  color: #5a5a5a;
  font-size: 14px;
`;

const EmptyStateBox = styled.div`
  text-align: center;
  padding: 32px 16px;
  background: #fafafa;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
`;

const EmptyTitle = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: #292929;
  margin: 0 0 4px;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #5a5a5a;
  margin: 0;
`;

const ErrorText = styled.p`
  color: #c53030;
  text-align: center;
  padding: 20px;
  font-size: 14px;
`;

// -- Types --

export type PlanTypeFilter = 'autoship' | 'membership';
export type ActiveCardLayout = 'grid' | 'list';

export interface SubscriptionsDashboardProps {
  readonly user: UserProfile;
  readonly planType: PlanTypeFilter;
  /** Full header node (title, optional back button, etc.) */
  readonly header: ReactNode;
  /** Layout used for the "Active Subscriptions" section. Past subs always use list. */
  readonly activeCardLayout?: ActiveCardLayout;
  readonly emptyState: {
    readonly title: string;
    readonly description: string;
  };
  readonly toasts: {
    /** Shown after cancellation feedback page completes. */
    readonly canceled: string;
  };
  readonly cancelFeedbackBanner: {
    /** Heading for cancellation feedback banner ("Your X has been canceled."). */
    readonly heading: string;
  };
  readonly retentionSuccessBanner: {
    /** Sentence fragment after "We've added them to your" (e.g., 'subscription' or 'membership'). */
    readonly productLabel: string;
  };
  /** Extra content rendered below the list sections (e.g., Priority Support help block). */
  readonly footer?: ReactNode;
}

// -- Helpers --

function buildSwapItems(
  oldItem: SubscriptionItem | null,
  product: { id: string; title: string; imageUrl?: string },
  variant: ProductVariant,
) {
  const newItem = {
    product_id: product.id,
    variation_id: variant.uuid,
    sku: variant.sku,
    name: product.title,
    quantity: 1,
    unit_price_base: variant.price.base,
    unit_price_display: variant.price.display,
    image_url: product.imageUrl ?? '',
  };
  return oldItem ? [{ item_id: oldItem.id, quantity: 0 }, newItem] : [newItem];
}

// -- Component --

export function SubscriptionsDashboard({
  user,
  planType,
  header,
  activeCardLayout = 'list',
  emptyState,
  toasts,
  cancelFeedbackBanner,
  retentionSuccessBanner,
  footer,
}: SubscriptionsDashboardProps) {
  const { data: subscriptions, isLoading, error } = useCustomerSubscriptions(user.id);
  const cancelMutation = useCancelSubscription(user.id);
  const skipMutation = useSkipSubscription(user.id);
  const rescheduleMutation = useRescheduleSubscription(user.id);
  const updateItemsMutation = useUpdateSubscriptionItems(user.id);

  const [manageTarget, setManageTarget] = useState<CustomerSubscription | null>(null);
  const [showRetentionOffer, setShowRetentionOffer] = useState(false);
  const [showRetentionSuccess, setShowRetentionSuccess] = useState(false);
  const [showCancelReasonPage, setShowCancelReasonPage] = useState(false);
  const [showCancelFeedback, setShowCancelFeedback] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [swapBrowse, setSwapBrowse] = useState(false);
  const [swapProductSlug, setSwapProductSlug] = useState<string | null>(null);
  const [pendingSwap, setPendingSwap] = useState<{
    product: { id: string; title: string; imageUrl?: string };
    variant: ProductVariant;
  } | null>(null);
  const [swapError, setSwapError] = useState<string | null>(null);
  const [variantTarget, setVariantTarget] = useState<CustomerSubscription | null>(null);
  const [skipTarget, setSkipTarget] = useState<CustomerSubscription | null>(null);
  const [rescheduleTarget, setRescheduleTarget] = useState<CustomerSubscription | null>(null);

  const { active, inactive } = useMemo(() => {
    if (!subscriptions) return { active: [], inactive: [] };
    const activeSubs: CustomerSubscription[] = [];
    const inactiveSubs: CustomerSubscription[] = [];
    for (const sub of subscriptions) {
      if (sub.plan.type !== planType) continue;
      if (sub.status === 'active' || sub.status === 'paused' || sub.status === 'pending') {
        activeSubs.push(sub);
      } else {
        inactiveSubs.push(sub);
      }
    }
    return { active: activeSubs, inactive: inactiveSubs };
  }, [subscriptions, planType]);

  const handleManageClick = useCallback((sub: CustomerSubscription) => {
    setManageTarget(sub);
  }, []);

  const handleCancelClick = useCallback((_sub: CustomerSubscription) => {
    setShowRetentionOffer(true);
  }, []);

  const handleAcceptOffer = useCallback(() => {
    setShowRetentionOffer(false);
    setShowRetentionSuccess(true);
  }, []);

  const handleDeclineOffer = useCallback(() => {
    setShowRetentionOffer(false);
    setShowCancelReasonPage(true);
  }, []);

  const handleRetentionOfferBack = useCallback(() => {
    setShowRetentionOffer(false);
  }, []);

  const handleRetentionFeedbackSubmit = useCallback(
    (_reason: string, _comments: string) => {
      setShowRetentionSuccess(false);
      setManageTarget(null);
      setToastMessage('Thank you for your feedback! Your offer has been applied.');
    },
    [],
  );

  const handleRetentionFeedbackReturn = useCallback(() => {
    setShowRetentionSuccess(false);
    setManageTarget(null);
    setToastMessage('Your offer has been applied.');
  }, []);

  const handleCancelConfirm = useCallback(
    (reason: string) => {
      if (!manageTarget) return;
      cancelMutation.mutate(
        { subscriptionId: manageTarget.id, reason },
        {
          onSuccess: () => {
            setShowCancelReasonPage(false);
            setShowCancelFeedback(true);
          },
        },
      );
    },
    [manageTarget, cancelMutation],
  );

  const handleCancelFeedbackSubmit = useCallback(
    (_reason: string, _comments: string) => {
      setShowCancelFeedback(false);
      setManageTarget(null);
      setToastMessage(toasts.canceled);
    },
    [toasts.canceled],
  );

  const handleCancelFeedbackReturn = useCallback(() => {
    setShowCancelFeedback(false);
    setManageTarget(null);
    setToastMessage(toasts.canceled);
  }, [toasts.canceled]);

  const handleCancelReasonReturn = useCallback(() => {
    setShowCancelReasonPage(false);
    setManageTarget(null);
  }, []);

  const handleSkipClick = useCallback((sub: CustomerSubscription) => {
    setSkipTarget(sub);
  }, []);

  const handleSkipConfirm = useCallback(() => {
    if (!skipTarget) return;
    skipMutation.mutate(
      { subscriptionId: skipTarget.id, reason: 'Skipped from manage page' },
      {
        onSuccess: () => {
          setSkipTarget(null);
          setManageTarget(null);
          setToastMessage('This shipment has been skipped. Your next order is rescheduled.');
        },
        onError: (err) => {
          setSkipTarget(null);
          setToastMessage(
            err instanceof Error
              ? err.message
              : 'Something went wrong while skipping. Please try again.',
          );
        },
      },
    );
  }, [skipTarget, skipMutation]);

  const handleSkipCancel = useCallback(() => {
    setSkipTarget(null);
  }, []);

  const handleRescheduleClick = useCallback((sub: CustomerSubscription) => {
    setRescheduleTarget(sub);
  }, []);

  const handleRescheduleConfirm = useCallback(
    (newDate: string) => {
      if (!rescheduleTarget) return;
      rescheduleMutation.mutate(
        { subscriptionId: rescheduleTarget.id, newDate },
        {
          onSuccess: () => {
            setRescheduleTarget(null);
            setToastMessage('Your subscription has been rescheduled.');
          },
        },
      );
    },
    [rescheduleTarget, rescheduleMutation],
  );

  const handleRescheduleCancel = useCallback(() => {
    setRescheduleTarget(null);
  }, []);

  const handleToastDismiss = useCallback(() => {
    setToastMessage(null);
  }, []);

  const handleSwapProduct = useCallback((_sub: CustomerSubscription) => {
    setSwapBrowse(true);
  }, []);

  const handleSelectSwapProduct = useCallback((product: CollectionProduct) => {
    setSwapProductSlug(product.slug);
    setSwapBrowse(false);
  }, []);

  const handleContinueSwap = useCallback(
    (product: { id: string; title: string; imageUrl?: string }, variant: ProductVariant) => {
      setPendingSwap({ product, variant });
    },
    [],
  );

  const handleConfirmSwap = useCallback(() => {
    if (!manageTarget || !pendingSwap) return;
    setSwapError(null);
    const items = buildSwapItems(
      manageTarget.items?.[0] ?? null,
      pendingSwap.product,
      pendingSwap.variant,
    );

    updateItemsMutation.mutate(
      { subscriptionId: manageTarget.id, items },
      {
        onSuccess: () => {
          setPendingSwap(null);
          setSwapProductSlug(null);
          setSwapBrowse(false);
          setSwapError(null);
          setToastMessage('Your next delivery has been updated!');
        },
        onError: (err) => {
          setSwapError(
            err instanceof Error ? err.message : 'Something went wrong. Please try again.',
          );
        },
      },
    );
  }, [manageTarget, pendingSwap, updateItemsMutation]);

  const handleCancelSwapDialog = useCallback(() => {
    setPendingSwap(null);
    setSwapError(null);
  }, []);

  const handleCancelDetailPage = useCallback(() => {
    setSwapProductSlug(null);
    setSwapBrowse(true);
  }, []);

  const handleSwapBrowseBack = useCallback(() => {
    setSwapBrowse(false);
  }, []);

  const handleChangeVariant = useCallback((sub: CustomerSubscription) => {
    setVariantTarget(sub);
  }, []);

  const handleVariantConfirm = useCallback(
    (variant: ProductVariant, product: { id: string; title: string; imageUrl?: string }) => {
      if (!variantTarget) return;
      const items = buildSwapItems(variantTarget.items?.[0] ?? null, product, variant);

      updateItemsMutation.mutate(
        { subscriptionId: variantTarget.id, items },
        {
          onSuccess: () => {
            setVariantTarget(null);
            setToastMessage('Your size and color preferences have been updated!');
          },
          onError: (err) => {
            setVariantTarget(null);
            setToastMessage(
              err instanceof Error
                ? err.message
                : 'Something went wrong while updating preferences. Please try again.',
            );
          },
        },
      );
    },
    [variantTarget, updateItemsMutation],
  );

  const handleVariantClose = useCallback(() => {
    setVariantTarget(null);
  }, []);

  const handleCloseManage = useCallback(() => {
    setManageTarget(null);
  }, []);

  // -- Retention Offer View --
  if (showRetentionOffer && manageTarget) {
    return (
      <RetentionOfferPage
        subscription={manageTarget}
        onAcceptOffer={handleAcceptOffer}
        onDeclineOffer={handleDeclineOffer}
        onBack={handleRetentionOfferBack}
      />
    );
  }

  // -- Retention Success + Feedback View --
  if (showRetentionSuccess && manageTarget) {
    const matchedFreq = manageTarget.plan.frequencies?.find(
      (f) => f.id === manageTarget.plan_frequency_id,
    );
    const priceNote = matchedFreq
      ? `Then $${(matchedFreq.price_display / 100).toFixed(2)} USD/month after until you cancel`
      : undefined;

    return (
      <RetentionFeedbackPage
        onSubmit={handleRetentionFeedbackSubmit}
        onReturn={handleRetentionFeedbackReturn}
        useSheetLayout
        confirmationBanner={{
          heading: "You're getting 2 FREE months!",
          description: `We've added them to your ${retentionSuccessBanner.productLabel} — no charges for the next 2 billing cycles.`,
          pricingNote: priceNote,
        }}
      />
    );
  }

  // -- Cancellation Feedback View (after cancel succeeds) --
  if (showCancelFeedback && manageTarget) {
    return (
      <RetentionFeedbackPage
        onSubmit={handleCancelFeedbackSubmit}
        onReturn={handleCancelFeedbackReturn}
        useSheetLayout
        confirmationBanner={{
          heading: cancelFeedbackBanner.heading,
          description: "You won't be charged again. A confirmation email is on the way.",
        }}
      />
    );
  }

  // -- Cancellation Reason View --
  if (showCancelReasonPage && manageTarget) {
    return (
      <CancellationReasonPage
        isLoading={cancelMutation.isPending}
        onConfirm={handleCancelConfirm}
        onReturn={handleCancelReasonReturn}
      />
    );
  }

  // -- Swap Detail View --
  if (manageTarget && swapProductSlug) {
    return (
      <>
        <SwapProductDetailPage
          productSlug={swapProductSlug}
          subscription={manageTarget}
          onContinueSwap={handleContinueSwap}
          onCancel={handleCancelDetailPage}
        />
        {pendingSwap && (
          <SwapConfirmationDialog
            subscription={manageTarget}
            newProduct={pendingSwap.product}
            isLoading={updateItemsMutation.isPending}
            error={swapError}
            onConfirm={handleConfirmSwap}
            onCancel={handleCancelSwapDialog}
          />
        )}
      </>
    );
  }

  // -- Swap Browse View --
  if (manageTarget && swapBrowse) {
    return (
      <SwapProductBrowsePage
        subscription={manageTarget}
        onSelectProduct={handleSelectSwapProduct}
        onBack={handleSwapBrowseBack}
      />
    );
  }

  // -- List View (modal overlays on top when manageTarget is set) --
  const ActiveCardsContainer = activeCardLayout === 'grid' ? CardGrid : CardList;

  return (
    <Container>
      {header}

      {toastMessage && <SuccessToast message={toastMessage} onDismiss={handleToastDismiss} />}

      {isLoading && <LoadingContainer>Loading subscriptions...</LoadingContainer>}

      {error && (
        <ErrorText>Unable to load your subscriptions. Please try again later.</ErrorText>
      )}

      {!isLoading && !error && subscriptions && (
        <>
          {active.length > 0 && (
            <Section>
              <SectionHeader>
                <SectionTitle $variant={activeCardLayout}>
                  Active Subscriptions ({active.length})
                </SectionTitle>
                <ActiveIcon>
                  <CheckCircle size={18} />
                </ActiveIcon>
              </SectionHeader>
              <ActiveCardsContainer>
                {active.map((sub) => (
                  <SubscriptionCard key={sub.id} subscription={sub} onManage={handleManageClick} />
                ))}
              </ActiveCardsContainer>
            </Section>
          )}

          {inactive.length > 0 && (
            <Section>
              <SectionHeader>
                <SectionTitle $variant={activeCardLayout}>Past Subscriptions</SectionTitle>
              </SectionHeader>
              <CardList>
                {inactive.map((sub) => (
                  <SubscriptionCard key={sub.id} subscription={sub} />
                ))}
              </CardList>
            </Section>
          )}

          {active.length === 0 && inactive.length === 0 && (
            <EmptyStateBox>
              <EmptyTitle>{emptyState.title}</EmptyTitle>
              <EmptyText>{emptyState.description}</EmptyText>
            </EmptyStateBox>
          )}
        </>
      )}

      {footer}

      {manageTarget && (
        <ManageSubscriptionPage
          subscription={manageTarget}
          onCancel={handleCancelClick}
          onSkip={handleSkipClick}
          onReschedule={handleRescheduleClick}
          onSwapProduct={handleSwapProduct}
          onChangeVariant={handleChangeVariant}
          onClose={handleCloseManage}
        />
      )}

      {variantTarget && (
        <ChangeVariantSheet
          subscription={variantTarget}
          isLoading={updateItemsMutation.isPending}
          onConfirm={handleVariantConfirm}
          onClose={handleVariantClose}
        />
      )}

      {skipTarget && (
        <SkipConfirmationSheet
          subscription={skipTarget}
          isLoading={skipMutation.isPending}
          onConfirm={handleSkipConfirm}
          onCancel={handleSkipCancel}
        />
      )}

      {rescheduleTarget && (
        <RescheduleSheet
          subscription={rescheduleTarget}
          isLoading={rescheduleMutation.isPending}
          onConfirm={handleRescheduleConfirm}
          onCancel={handleRescheduleCancel}
        />
      )}
    </Container>
  );
}
